import {useState} from 'react'
import {View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {MAX_ALT_TEXT} from '#/lib/constants'
import {DOCUMENT_TITLE_MAX_LENGTH} from '#/lib/document/limits'
import {
  type DocumentMaterial,
  type DocumentPageMaterial,
} from '#/lib/document/material'
import {type OptimizeLevel} from '#/lib/document/optimize'
import {
  type DocumentAudience,
  type DocumentEmbedView,
} from '#/lib/document/types'
import {useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {type DialogControlProps} from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import {PdfCarousel} from '#/components/Post/Embed/document/PdfCarousel.web'
import {Text} from '#/components/Typography'
import {type DocumentCompression} from '../state/composer'
import {type DocumentState} from '../state/document'
import {DocumentPreview} from './DocumentPreview'

/**
 * Web preview of a picked document. Once the upload finishes and we have the
 * local bytes, this renders the *actual* published carousel against them, so
 * what the user sees in the composer is exactly what will ship in the post.
 */
export function DocumentLivePreview({
  document,
  onRemove,
  altText,
  onEditAlt,
  onEditTitle,
  audience,
  compression,
  onChangeCompressionLevel,
  material,
  onChangePageMaterial,
}: {
  document: DocumentState
  onRemove: () => void
  altText: string
  onEditAlt: (alt: string) => void
  onEditTitle: (title: string) => void
  /** Chosen in the audience dialog; shown here so the author sees the card. */
  audience?: DocumentAudience
  compression?: DocumentCompression
  onChangeCompressionLevel: (level: OptimizeLevel) => void
  /** What the author has put on the document's pages so far. */
  material?: DocumentMaterial
  /**
   * Commits one page's material into the composer.
   *
   * The carousel is the author's — with this in hand it grows the `+` control and
   * the docked bar, and its questions play as a preview that is never recorded.
   */
  onChangePageMaterial?: (
    page: number,
    material: DocumentPageMaterial | undefined,
  ) => void
}) {
  const {_} = useLingui()
  const {currentAccount} = useSession()
  const altControl = Dialog.useDialogControl()
  const titleControl = Dialog.useDialogControl()

  const isPdf = document.file.mimeType === 'application/pdf'
  /*
   * The bytes are the *local* file, and a document opened from a draft has none
   * — the upload finished long ago, so its bytes are the PDS's and are named by
   * CID. Both cases render the same carousel; only where it reads the pages from
   * differs, which is why the CID is handed over when there are no local bytes.
   */
  const bytes = document.file.bytes

  if (document.status !== 'done' || !isPdf) {
    return (
      <DocumentPreview
        document={document}
        onRemove={onRemove}
        compression={compression}
      />
    )
  }

  const pending = document.pendingPublish
  const embed: DocumentEmbedView = {
    $type: 'app.bsky.embed.document#view',
    // Empty while the local file is what is on screen: the carousel then reads
    // the bytes it was handed and never builds a fetchable address.
    cid: bytes ? '' : pending.blob.ref.$link,
    mimeType: pending.mimeType,
    title: pending.title,
    pageCount: pending.pageCount,
    fileSize: pending.fileSize,
    alt: altText,
  }

  return (
    <>
      <PdfCarousel
        embed={embed}
        // Whose repo the document's bytes are in — the author's own, either way.
        authorDid={currentAccount?.did ?? ''}
        preview={
          bytes
            ? {
                cacheKey: document.file.uri,
                bytes,
              }
            : undefined
        }
        audience={audience}
        compression={compression}
        onChangeCompressionLevel={onChangeCompressionLevel}
        onRemove={onRemove}
        onEditAlt={() => altControl.open()}
        onEditTitle={() => titleControl.open()}
        altOverride={altText}
        pageMaterial={material}
        onChangePageMaterial={onChangePageMaterial}
        isPreview
      />
      <DocumentTextDialog
        control={titleControl}
        heading={_(msg`Document title`)}
        label={_(msg`Title`)}
        initialValue={pending.title}
        maxLength={DOCUMENT_TITLE_MAX_LENGTH}
        // An untitled document would publish an empty title, so require one.
        requireValue
        onSubmit={onEditTitle}
      />
      <DocumentTextDialog
        control={altControl}
        heading={_(msg`Descriptive alt text`)}
        label={_(msg`Alt text`)}
        initialValue={altText}
        multiline
        warnAbove={MAX_ALT_TEXT}
        warnLabel={_(msg`Alt text will be truncated.`)}
        onSubmit={onEditAlt}
      />
    </>
  )
}

function DocumentTextDialog({
  control,
  heading,
  label,
  initialValue,
  maxLength,
  multiline = false,
  warnAbove,
  warnLabel,
  requireValue = false,
  onSubmit,
}: {
  control: DialogControlProps
  heading: string
  label: string
  initialValue: string
  maxLength?: number
  multiline?: boolean
  /** Show {@link warnLabel} once the draft passes this length */
  warnAbove?: number
  warnLabel?: string
  /** Refuse to submit an empty value */
  requireValue?: boolean
  onSubmit: (value: string) => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const [draft, setDraft] = useState(initialValue)
  const value = draft.trim()
  const canSubmit = !requireValue || value.length > 0

  return (
    <Dialog.Outer
      control={control}
      onClose={() => {
        if (canSubmit) onSubmit(value)
      }}
      nativeOptions={{fullHeight: true}}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={heading}>
        <View style={[a.mt_md, a.gap_md]}>
          <View style={[a.gap_sm]}>
            <TextField.LabelText>{heading}</TextField.LabelText>
            <TextField.Root>
              <Dialog.Input
                label={label}
                placeholder=""
                onChangeText={setDraft}
                defaultValue={initialValue}
                maxLength={maxLength}
                multiline={multiline}
                numberOfLines={multiline ? 3 : 1}
                autoFocus
              />
            </TextField.Root>
            {warnAbove !== undefined && draft.length > warnAbove && (
              <Text style={[a.text_xs, a.italic, t.atoms.text_contrast_medium]}>
                {warnLabel}
              </Text>
            )}
          </View>
          <Button
            label={_(msg`Save`)}
            size="large"
            color="primary"
            variant="solid"
            disabled={!canSubmit}
            onPress={() => control.close()}
            style={[a.flex_grow]}>
            <ButtonText>
              <Trans>Save</Trans>
            </ButtonText>
          </Button>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}
